<!--
/**
 * APCWO customisation for OrangeHRM — Email Templates module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          Email Templates
        </oxd-text>
        <oxd-button
          label="Add"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :loading="isLoading"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';

const EVENT_TYPE_LABELS = {
  birthday: 'Birthday',
  work_anniversary: 'Work Anniversary',
  wedding_anniversary: 'Wedding Anniversary',
  public_holiday: 'Public Holiday',
  generic: 'Generic',
};
const STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  inactive: 'Inactive',
};

export default {
  name: 'EmailTemplateList',
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/apcwo/email-templates',
    );
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http);
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
    };
  },
  data() {
    return {
      headers: [
        {name: 'name', title: 'Name', style: {'flex-basis': '24%'}},
        {name: 'category', title: 'Category', style: {'flex-basis': '16%'}},
        {
          name: 'eventType',
          title: 'Event',
          style: {'flex-basis': '16%'},
          getValue: (item) =>
            EVENT_TYPE_LABELS[item.eventType] || item.eventType,
        },
        {
          name: 'status',
          title: 'Status',
          style: {'flex-basis': '12%'},
          getValue: (item) => STATUS_LABELS[item.status] || item.status,
        },
        {
          name: 'isSent',
          title: 'Sent',
          style: {'flex-basis': '10%'},
          getValue: (item) => (item.isSent ? 'Yes' : 'No'),
        },
        {
          name: 'actions',
          title: 'Actions',
          style: {'flex-basis': '12%'},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            delete: {
              onClick: this.onClickDelete,
              component: 'oxd-icon-button',
              props: {name: 'trash'},
            },
            edit: {
              onClick: this.onClickEdit,
              props: {name: 'pencil-fill'},
            },
          },
        },
      ],
      checkedItems: [],
    };
  },
  methods: {
    onClickAdd() {
      navigate('/admin/saveEmailTemplate');
    },
    onClickEdit(item) {
      navigate('/admin/saveEmailTemplate/{id}', {id: item.id});
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => this.items?.data[index].id);
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (!(items instanceof Array)) return;
      this.isLoading = true;
      this.http
        .deleteAll({ids: items})
        .then(() => this.$toast.deleteSuccess())
        .then(() => {
          this.isLoading = false;
          this.resetDataTable();
        });
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
  },
};
</script>
